import { rewriteRequestsPath } from '@gc-bridge/interceptors';
import { authProvider } from '@gc-bridge/services';
import { useCurrentUserStore } from '@gc-bridge/stores/currentUser';
import { AdditionalColors } from '@gc-bridge/theme/colors';
import {
  AdditionalInfoSectionProps,
  isRemoteInMaintenanceMode,
  RemoteComponent,
} from '@gcportal/base';
import { Box, CircularProgress, Divider, styled, Typography } from '@mui/material';
import { Suspense, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { MaintenanceMode } from '../MaintenanceMode';

interface Props {
  section: AdditionalInfoSectionProps;
  components: RemoteComponent[];
}

const Title = styled(Box)({
  color: AdditionalColors.Black,
  fontFamily: 'Lato',
  fontSize: '40px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: 'normal',
});

export function AdditionalInfoSection({ section, components }: Props) {
  const { user } = useCurrentUserStore();

  const renderComponent = useCallback(
    ({
      element: Element,
      shouldPassAuthFunction,
      remoteDisplayName,
      maintenance,
      maintenanceBypassGroups,
    }: RemoteComponent) => {
      const remoteIsInMaintenance = isRemoteInMaintenanceMode(
        { maintenance, maintenanceBypassGroups },
        user?.groups
      );

      if (remoteIsInMaintenance) {
        return (
          <MaintenanceMode
            key={remoteDisplayName}
            remoteDisplayName={remoteDisplayName}
          />
        );
      }

      return (
        <ErrorBoundary
          fallback={
            <Typography color="error">
              Could not load content from <strong>{remoteDisplayName}</strong>
            </Typography>
          }
          key={remoteDisplayName}
        >
          <Suspense fallback={<CircularProgress size={30} />}>
            <Typography variant="h5" fontWeight={700} fontSize="14px">
              {remoteDisplayName}
            </Typography>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-expect-error */}
            <Element
              rewriteRequestsPath={rewriteRequestsPath}
              {...(shouldPassAuthFunction ? { shellProvidedAuth: authProvider } : {})}
            />
          </Suspense>
        </ErrorBoundary>
      );
    },
    [user?.groups]
  );

  if (!components.length) {
    return;
  }

  return (
    <Box sx={{ flexGrow: section.flexGrow, flexBasis: 0, padding: '30px 50px 50px' }}>
      <Title>{section.title}</Title>
      <Divider sx={{ width: '131px', height: '2px', bgcolor: 'primary.main', my: 1 }} />
      <Box>{components.map(renderComponent)}</Box>
    </Box>
  );
}
